import React from 'react'
import {
  APPOINTMENT_CREATE,
  APPOINTMENT_EDIT,
  BILLING_CREATE,
  BILLING_EDIT,
  CLIENT_CREATE,
  CLIENT_EDIT,
  HOME_PAGE,
  INVENTORY_ADJUST,
  INVENTORY_CREATE,
  INVENTORY_HOLD,
  INVENTORY_LOCATION_TRANSFER,
  INVENTORY_RELEASE,
  INVENTORY_TRANSFER,
  INVENTORY_TRANSFER_SKU2SKU,
  INVOICE_DETAILS,
  INVOICE_EDIT,
  ITEM_CREATE,
  ITEM_EDIT,
  ITEM_VIEW,
  LOCATION_CREATE,
  LOCATION_EDIT,
  MENU_APPOINTMENT,
  MENU_BARCODE_GENERATOR,
  MENU_BILLING_CLIENT_PRICING,
  MENU_BILLING_INVOICES,
  MENU_CARRIER,
  MENU_CLIENTS,
  MENU_COUNTRY,
  MENU_DASHBOARD,
  MENU_DEPARTMENTS,
  MENU_ERRORS,
  MENU_HOLIDAY,
  MENU_INVENTORY,
  MENU_ITEM,
  MENU_JOBS,
  MENU_LANGUAGES,
  MENU_LOCATION,
  MENU_LOGS,
  MENU_MOBILE_INVENTORY,
  MENU_MOBILE_ITEMS,
  MENU_MOBILE_RECEIVING,
  MENU_MOBILE_TODO,
  MENU_ORDERS,
  MENU_ORDER_ACTIVITY,
  MENU_PACKING,
  MENU_PERMISSIONS,
  MENU_PLATFORMS,
  MENU_PROFILE,
  MENU_PROVINCE,
  MENU_PURCHASE_ORDER,
  MENU_RECEIVING,
  MENU_RECEIVING_TRANSACTIONS,
  MENU_RETURN,
  MENU_ROLES,
  MENU_SERIAL_NUMBER,
  MENU_SETTINGS,
  MENU_SHIPPING,
  MENU_SUPPLIER,
  MENU_SUPPLY,
  MENU_TAXES,
  MENU_TAX_CLASSES,
  MENU_TAX_RATES,
  MENU_TODO,
  MENU_TRANSACTIONS,
  MENU_USER,
  MENU_WAREHOUSE,
  MENU_ZONES,
  ORDERS_CREATE,
  ORDERS_EDIT,
  ORDERS_MODIFY_ADDRESS,
  ORDERS_VIEW,
  PERMISSIONS_CREATE,
  PERMISSIONS_EDIT,
  PURCHASE_ORDER_CREATE,
  PURCHASE_ORDER_EDIT,
  RECEIVING_CREATE,
  RECEIVING_DETAILS,
  RECEIVING_EDIT,
  RETURN_CREATE,
  RETURN_EDIT,
  ROLES_CREATE,
  ROLES_EDIT,
  SYSTEM_CREATE,
  SYSTEM_EDIT,
  USER_CREATE,
  USER_EDIT,
  WAREHOUSE_CREATE,
  WAREHOUSE_EDIT,
  ZONE_CREATE,
  ZONE_EDIT,
} from './constants/PermissionConstants'
import FailedJobLogsList from './views/logs/failed-job-logs/FailedJobLogsList'
import OrderLogs from './views/logs/order-logs/OrderLogs'
import QueueLogsList from './views/logs/queue-logs/QueueLogsList'
import ScheduleItemLogsList from './views/logs/schedule-item-logs/ScheduleItemLogsList'
import ScheduleLogsList from './views/logs/schedule-logs/ScheduleLogsList'

const Home = React.lazy(() => import('./views/home/Home'))
const Dashboard = React.lazy(() => import('./views/dashboards/dashboard/Dashboard'))
const OrderActivity = React.lazy(() => import('./views/dashboards/order-activity/OrderActivity'))
const Returns = React.lazy(() => import('./views/return/Returns'))
const ImportReturn = React.lazy(() => import('./views/return/import/ImportReturn'))
const CreateReturn = React.lazy(() => import('./views/return/Return'))
const OrderPicking = React.lazy(() => import('./views/picking/order-picking/OrderPicking'))
const BatchPicking = React.lazy(() => import('./views/picking/batch-picking/BatchPicking'))
const PickingPacking = React.lazy(() => import('./views/picking/picking-packing/PickingPacking'))
const Replenishment = React.lazy(() => import('./views/replenishment/Replenishment'))
const Transfer = React.lazy(() => import('./views/transfer/Transfer'))
const Packing = React.lazy(() => import('./views/packing/Packing'))
const Shippings = React.lazy(() => import('./views/shipping/Shippings'))
const TransferPackage = React.lazy(() => import('./views/shipping/TransferPackage'))
const Jobs = React.lazy(() => import('./views/jobs/Jobs'))
const JobPickingDetails = React.lazy(() => import('./views/jobs/PickingDetails'))
const JobPackingDetails = React.lazy(() => import('./views/jobs/PackingDetails'))
const JobReplenishmentDetails = React.lazy(() => import('./views/jobs/ReplenishmentDetails'))
const JobTransferDetails = React.lazy(() => import('./views/jobs/TransferDetails'))
const ImportJobs = React.lazy(() => import('./views/jobs/import/ImportJobs'))
const Permissions = React.lazy(() => import('./views/security/permissions/Permissions'))
const EditPermission = React.lazy(() => import('./views/security/permissions/Permission'))
const CreatePermission = React.lazy(() => import('./views/security/permissions/Permission'))
const Roles = React.lazy(() => import('./views/security/roles/Roles'))
const EditRole = React.lazy(() => import('./views/security/roles/Role'))
const CreateRole = React.lazy(() => import('./views/security/roles/Role'))
const UsersList = React.lazy(() => import('./views/security/users/Users'))
const EditUser = React.lazy(() => import('./views/security/users/User'))
const AddClientUser = React.lazy(() => import('./views/security/users/AddClientUser'))
const Clients = React.lazy(() => import('./views/clients/clients/Clients'))
const ImportClients = React.lazy(() => import('./views/clients/clients/import/ImportClients'))
const CreateClient = React.lazy(() => import('./views/clients/clients/AddClient'))
const EditClient = React.lazy(() => import('./views/clients/clients/Client'))
const Locations = React.lazy(() => import('./views/locations/Locations'))
const ImportLocations = React.lazy(() => import('./views/locations/import/ImportLocations'))
const CreateLocation = React.lazy(() => import('./views/locations/AddLocation'))
const EditLocation = React.lazy(() => import('./views/locations/Location'))
const PrintBulk = React.lazy(() => import('./views/locations/partials/PrintBulk'))
const Items = React.lazy(() => import('./views/items/items/Items'))
const ItemDetails = React.lazy(() => import('./views/items/items/ItemDetailsMobile'))
const CreateItem = React.lazy(() => import('./views/items/items/Item'))
const ImportItem = React.lazy(() => import('./views/items/items/import/ImportItem'))
const Barcode = React.lazy(() => import('./views/items/barcode/Barcode'))
const Orders = React.lazy(() => import('./views/orders/Orders'))
const ImportOrder = React.lazy(() => import('./views/orders/import/ImportOrder'))
const CreateOrder = React.lazy(() => import('./views/orders/AddOrder'))
const EditOrder = React.lazy(() => import('./views/orders/AddOrder'))
const PurchaseOrders = React.lazy(() => import('./views/purchase-order/PurchaseOrders'))
const ImportPurchaseOrder = React.lazy(() =>
  import('./views/purchase-order/import/ImportPurchaseOrder'),
)
const CreatePurchaseOrder = React.lazy(() => import('./views/purchase-order/AddPurchaseOrder'))
const EditPurchaseOrder = React.lazy(() => import('./views/purchase-order/EditPurchaseOrder'))
const AppointmentCalendar = React.lazy(() => import('./views/appointments/AppointmentCalendar'))
const ImportAppointment = React.lazy(() => import('./views/appointments/import/ImportAppointment'))
const Appointments = React.lazy(() => import('./views/appointments/Appointments'))
const CreateAppointment = React.lazy(() => import('./views/appointments/Appointment'))
const EditAppointment = React.lazy(() => import('./views/appointments/Appointment'))
const Receivings = React.lazy(() => import('./views/receiving/Receivings'))
const ImportReceiving = React.lazy(() => import('./views/receiving/import/ImportReceiving'))
const Receiving = React.lazy(() => import('./views/receiving/Receiving'))
const ReceivingView = React.lazy(() => import('./views/receiving/ReceivingView'))
const ReceivingMobile = React.lazy(() => import('./views/receiving/ReceivingMobile'))
const EditReceivingMobile = React.lazy(() => import('./views/receiving/EditReceivingMobile'))
const Inventory = React.lazy(() => import('./views/inventory/inventory/Inventory'))
const ImportInventory = React.lazy(() =>
  import('./views/inventory/inventory/import/ImportInventory'),
)
const TransferSku2Sku = React.lazy(() => import('./views/inventory/inventory/TransferSku2Sku'))
const AdjustInventory = React.lazy(() => import('./views/inventory/inventory/AdjustInventory'))
const TransferInventory = React.lazy(() => import('./views/inventory/inventory/TransferInventory'))
const HoldInventory = React.lazy(() => import('./views/inventory/inventory/HoldInventory'))
const ReleaseInventory = React.lazy(() => import('./views/inventory/inventory/ReleaseInventory'))
const LocationTransfer = React.lazy(() => import('./views/inventory/inventory/LocationTransfer'))
const Transactions = React.lazy(() => import('./views/inventory/transactions/Transactions'))
const ReceivingTransactions = React.lazy(() =>
  import('./views/inventory/receiving-transactions/ReceivingTransactions'),
)
const Zones = React.lazy(() => import('./views/zones/Zones'))
const ZoneCreate = React.lazy(() => import('./views/zones/Zone'))
const ZoneEdit = React.lazy(() => import('./views/zones/Zone'))
const Countries = React.lazy(() => import('./views/system/countries/Countries'))
const EditCountry = React.lazy(() => import('./views/system/countries/Country'))
const Languages = React.lazy(() => import('./views/system/languages/LanguagesList'))
const EditLanguage = React.lazy(() => import('./views/system/languages/Language'))
const Invoices = React.lazy(() => import('./views/billings/invoices/Invoices'))
const EditInvoice = React.lazy(() => import('./views/billings/invoices/Invoice'))
const ViewInvoice = React.lazy(() => import('./views/billings/invoices/Invoice'))
const TaxClasses = React.lazy(() => import('./views/billings/taxes/tax-classes/TaxClasses'))
const CreateTaxClass = React.lazy(() => import('./views/billings/taxes/tax-classes/TaxClass'))
const EditTaxClass = React.lazy(() => import('./views/billings/taxes/tax-classes/TaxClass'))
const TaxRates = React.lazy(() => import('./views/billings/taxes/tax-rates/TaxRates'))
const CreateTaxRate = React.lazy(() => import('./views/billings/taxes/tax-rates/AddTaxRates'))
const EditTaxRate = React.lazy(() => import('./views/billings/taxes/tax-rates/TaxRate'))
const Settings = React.lazy(() => import('./views/system/settings/Settings'))
const Warehouses = React.lazy(() => import('./views/warehouses/Warehouses'))
const EditWarehouse = React.lazy(() => import('./views/warehouses/Warehouse'))
const EditSetting = React.lazy(() => import('./views/system/settings/Setting'))
const Provinces = React.lazy(() => import('./views/system/provinces/Provinces'))
const EditProvince = React.lazy(() => import('./views/system/provinces/Province'))
const Carriers = React.lazy(() => import('./views/system/carriers/CarriersList'))
const EditCarrier = React.lazy(() => import('./views/system/carriers/Carrier'))
const Profile = React.lazy(() => import('./views/profile/ProfileForm'))
const Suppliers = React.lazy(() => import('./views/clients/suppliers/Suppliers'))
const EditSupplier = React.lazy(() => import('./views/clients/suppliers/Supplier'))
const CreateSupplier = React.lazy(() => import('./views/clients/suppliers/AddSupplier'))
const CreateSupply = React.lazy(() => import('./views/clients/supply/CreateSupply'))
const Holidays = React.lazy(() => import('./views/system/holidays/HolidaysList'))
const ImportHolidays = React.lazy(() => import('./views/system/holidays/import/ImportHolidays'))
const EditHoliday = React.lazy(() => import('./views/system/holidays/Holiday'))
const Errors = React.lazy(() => import('./views/system/errors/ErrorsList'))
const EditError = React.lazy(() => import('./views/system/errors/Error'))
const SerialNumber = React.lazy(() => import('./views/serial-number/SerialNumber'))
const BillingClientPricing = React.lazy(() =>
  import('./views/billings/client-pricing/ClientPricings'),
)
const CreateBillingClientPricing = React.lazy(() =>
  import('./views/billings/client-pricing/AddClientPricing'),
)
const Departments = React.lazy(() => import('./views/system/departments/DepartmentsList'))
const AddDepartment = React.lazy(() => import('./views/system/departments/DepartmentForm'))
const EditDepartment = React.lazy(() => import('./views/system/departments/DepartmentForm'))
const Platforms = React.lazy(() => import('./views/system/platforms/PlatformsList'))
const EditPlatform = React.lazy(() => import('./views/system/platforms/Platform'))
const Todo = React.lazy(() => import('./views/todo/TodoMobile'))
const Jira = React.lazy(() => import('./views/jira/Jira'))

const routes = [
  {
    path: '/',
    name: 'Home',
    exact: true,
    element: Home,
    permissions: [HOME_PAGE],
  },
  {
    path: '/dashboards/dashboard',
    name: 'Dashboard',
    exact: true,
    element: Dashboard,
    permissions: [MENU_DASHBOARD],
  },
  {
    path: '/dashboards/order-activity',
    name: 'Order Activity',
    exact: true,
    element: OrderActivity,
    permissions: [MENU_ORDER_ACTIVITY],
  },
  { path: '/returns', name: 'Returns', element: Returns, permissions: [MENU_RETURN] },
  {
    path: '/returns/import',
    name: 'Import',
    exact: true,
    element: ImportReturn,
    permissions: [RETURN_CREATE],
  },
  {
    path: '/returns/create',
    name: 'Create',
    exact: true,
    element: CreateReturn,
    permissions: [RETURN_CREATE],
  },
  {
    path: '/returns/edit/:id',
    name: 'Edit',
    exact: true,
    element: CreateReturn,
    permissions: [RETURN_EDIT],
  },
  {
    path: '/returns/view/:id',
    exact: true,
    name: 'View',
    element: CreateReturn,
    permissions: [MENU_RETURN],
  },
  {
    path: '/todo',
    name: 'Todo',
    element: Todo,
    permissions: [MENU_MOBILE_TODO, MENU_TODO],
  },
  {
    path: '/jira',
    name: 'Jira',
    element: Jira,
    permissions: [MENU_TODO],
  },
  {
    path: '/picking/order-picking/:id',
    name: 'Order Picking',
    element: OrderPicking,
    private: true,
    permissions: [MENU_MOBILE_TODO, MENU_TODO],
  },
  {
    path: '/picking/batch-picking/:id',
    name: 'Batch Picking',
    element: BatchPicking,
    private: true,
    permissions: [MENU_MOBILE_TODO, MENU_TODO],
  },
  {
    path: '/picking/picking-packing/:id',
    name: 'Picking Packing',
    element: PickingPacking,
    private: true,
    permissions: [MENU_MOBILE_TODO, MENU_TODO],
  },
  {
    path: '/replenishment/:id',
    name: 'Replenishment',
    element: Replenishment,
    permissions: [MENU_MOBILE_TODO, MENU_TODO],
  },
  {
    path: '/put-away/:id',
    name: 'Put Away',
    element: Transfer,
    private: true,
    permissions: [MENU_MOBILE_TODO, MENU_TODO],
  },
  {
    path: '/move-sheet/:id',
    name: 'Move Sheet',
    element: Transfer,
    private: true,
    permissions: [MENU_MOBILE_TODO, MENU_TODO],
  },
  { path: '/packing', name: 'Packing', element: Packing, permissions: [MENU_PACKING] },
  { path: '/shipping', name: 'Shipping', element: Shippings, permissions: [MENU_SHIPPING] },
  {
    path: '/serial-number',
    name: 'Serial Number',
    element: SerialNumber,
    permissions: [MENU_SERIAL_NUMBER],
  },
  {
    path: '/shipping/transfer-package',
    name: 'Transfer Package',
    exact: true,
    element: TransferPackage,
    permissions: [MENU_SHIPPING],
  },
  { path: '/jobs', name: 'Jobs', element: Jobs, permissions: [MENU_JOBS] },
  {
    path: '/jobs/picking-details/:id',
    name: 'Picking Details',
    element: JobPickingDetails,
    permissions: [MENU_JOBS],
  },
  {
    path: '/jobs/packing-details/:id',
    name: 'Packing Details',
    element: JobPackingDetails,
    permissions: [MENU_JOBS],
  },
  {
    path: '/jobs/replenishment-details/:id',
    name: 'Replenishment Details',
    element: JobReplenishmentDetails,
    permissions: [MENU_JOBS],
  },
  {
    path: '/jobs/transfer-details/:id',
    name: 'Transfer Details',
    element: JobTransferDetails,
    permissions: [MENU_JOBS],
  },
  { path: '/jobs/import', name: 'Import Jobs', element: ImportJobs, permissions: [MENU_JOBS] },
  {
    path: '/security/permissions',
    exact: true,
    name: 'Permissions',
    element: Permissions,
    permissions: [MENU_PERMISSIONS],
  },
  {
    path: '/security/permissions/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditPermission,
    permissions: [PERMISSIONS_EDIT],
  },
  {
    path: '/security/permissions/create',
    exact: true,
    name: 'Create',
    element: CreatePermission,
    permissions: [PERMISSIONS_CREATE],
  },
  {
    path: '/security/roles',
    exact: true,
    name: 'Roles',
    element: Roles,
    permissions: [MENU_ROLES],
  },
  {
    path: '/security/roles/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditRole,
    private: true,
    permissions: [ROLES_EDIT],
  },
  {
    path: '/security/roles/create',
    exact: true,
    name: 'Create',
    element: CreateRole,
    permissions: [ROLES_CREATE],
  },
  {
    path: '/security/users/users-list',
    exact: true,
    name: 'User List',
    element: UsersList,
    permissions: [MENU_USER],
  },
  {
    path: '/security/users/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditUser,
    permissions: [USER_EDIT],
  },
  {
    path: '/security/users/create',
    exact: true,
    name: 'Create',
    element: EditUser,
    permissions: [USER_CREATE],
  },
  {
    path: '/security/users/create-client-user/:id',
    exact: true,
    name: 'Create',
    element: AddClientUser,
    permissions: [MENU_USER],
  },
  {
    path: '/clients/clients',
    name: 'Clients',
    exact: true,
    element: Clients,
    permissions: [MENU_CLIENTS],
  },
  {
    path: '/clients/clients/import',
    name: 'Import',
    exact: true,
    element: ImportClients,
    permissions: [CLIENT_CREATE],
  },
  {
    path: '/clients/clients/create',
    exact: true,
    name: 'Create',
    element: CreateClient,
    permissions: [CLIENT_CREATE],
  },
  {
    path: '/clients/clients/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditClient,
    permissions: [CLIENT_EDIT],
  },
  {
    path: '/suppliers',
    name: 'Suppliers',
    exact: true,
    element: Suppliers,
    permissions: [MENU_SUPPLIER],
  },
  {
    path: '/suppliers/create',
    exact: true,
    name: 'Create',
    element: CreateSupplier,
    permissions: [MENU_SUPPLIER],
  },
  {
    path: '/suppliers/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditSupplier,
    permissions: [MENU_SUPPLIER],
  },
  {
    path: '/clients/supply',
    name: 'Create',
    exact: true,
    element: CreateSupply,
    permissions: [MENU_SUPPLY],
  },
  {
    path: '/locations',
    name: 'Locations',
    exact: true,
    element: Locations,
    permissions: [MENU_LOCATION],
  },
  {
    path: 'locations/create',
    exact: true,
    name: 'Create',
    element: CreateLocation,
    permissions: [LOCATION_CREATE],
  },
  {
    path: '/locations/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditLocation,
    permissions: [LOCATION_EDIT],
  },
  {
    path: '/locations/print-bulk',
    exact: true,
    name: 'Print Bulk Barcode',
    element: PrintBulk,
    permissions: [MENU_LOCATION],
  },
  {
    path: '/locations/import',
    exact: true,
    name: 'Import',
    element: ImportLocations,
    permissions: [LOCATION_CREATE],
  },
  {
    path: '/products/items',
    name: 'Items',
    exact: true,
    element: Items,
    permissions: [MENU_ITEM, MENU_MOBILE_ITEMS],
  },
  {
    path: '/products/items/details/:id',
    name: 'Item Details',
    exact: true,
    element: ItemDetails,
    permissions: [ITEM_EDIT, ITEM_VIEW],
  },
  {
    path: '/products/items/create',
    name: 'Items',
    exact: true,
    element: CreateItem,
    permissions: [ITEM_CREATE],
  },
  {
    path: '/products/items/edit/:id',
    name: 'Edit',
    exact: true,
    element: CreateItem,
    permissions: [ITEM_EDIT],
  },
  {
    path: '/products/items/view/:id',
    name: 'View',
    exact: true,
    element: CreateItem,
    permissions: [ITEM_VIEW],
  },
  {
    path: '/products/items/import',
    name: 'Import',
    exact: true,
    element: ImportItem,
    permissions: [ITEM_CREATE],
  },
  {
    path: '/products/barcode',
    name: 'Barcode Generator',
    exact: true,
    element: Barcode,
    permissions: [MENU_BARCODE_GENERATOR],
  },
  {
    path: '/appointments/calendar',
    name: 'Calendar',
    exact: true,
    element: AppointmentCalendar,
    permissions: [MENU_APPOINTMENT],
  },
  {
    path: '/appointments/import',
    name: 'Import',
    exact: true,
    element: ImportAppointment,
    permissions: [APPOINTMENT_CREATE],
  },
  {
    path: '/appointments',
    name: 'Appointments',
    exact: true,
    element: Appointments,
    permissions: [MENU_APPOINTMENT],
  },
  {
    path: '/appointments/create',
    name: 'Create',
    exact: true,
    element: CreateAppointment,
    permissions: [APPOINTMENT_CREATE],
  },
  {
    path: '/appointments/edit/:id',
    name: 'Edit',
    exact: true,
    element: EditAppointment,
    permissions: [APPOINTMENT_EDIT],
  },
  { path: '/orders', name: 'Orders', exact: true, element: Orders, permissions: [MENU_ORDERS] },
  {
    path: '/orders/import',
    name: 'Import',
    exact: true,
    element: ImportOrder,
    permissions: [ORDERS_CREATE],
  },
  {
    path: '/orders/create',
    name: 'Create',
    exact: true,
    element: CreateOrder,
    permissions: [ORDERS_CREATE],
  },
  {
    path: '/orders/edit/:id',
    name: 'Edit',
    exact: true,
    element: EditOrder,
    permissions: [ORDERS_EDIT],
  },
  {
    path: '/orders/modify-address/:id',
    name: 'Edit',
    exact: true,
    element: EditOrder,
    permissions: [ORDERS_MODIFY_ADDRESS],
  },
  {
    path: '/orders/view/:id',
    name: 'View',
    exact: true,
    element: EditOrder,
    permissions: [ORDERS_VIEW],
  },
  {
    path: '/receiving',
    name: 'Receiving',
    exact: true,
    element: Receivings,
    permissions: [MENU_RECEIVING],
  },
  {
    path: '/receiving/import',
    name: 'Import',
    exact: true,
    element: ImportReceiving,
    permissions: [RECEIVING_CREATE],
  },
  {
    path: '/receiving/appointment/:appointmentId',
    name: 'Receiving',
    exact: true,
    element: Receivings,
    permissions: [MENU_RECEIVING],
  },
  {
    path: '/receiving/create',
    name: 'Create',
    exact: true,
    element: Receiving,
    permissions: [RECEIVING_CREATE],
  },
  {
    path: '/receiving/create/:appointmentId',
    name: 'Create',
    exact: true,
    element: Receiving,
    permissions: [RECEIVING_CREATE],
  },
  {
    path: '/receiving/edit/:id',
    name: 'Edit',
    exact: true,
    element: Receiving,
    permissions: [RECEIVING_EDIT],
  },
  {
    path: '/receiving/view/:id',
    name: 'View',
    exact: true,
    element: ReceivingView,
    permissions: [RECEIVING_DETAILS],
  },
  {
    path: '/receiving-mobile',
    name: 'Mobile Receiving',
    exact: true,
    element: ReceivingMobile,
    permissions: [MENU_MOBILE_RECEIVING],
  },
  {
    path: '/receiving-mobile/:id',
    name: 'Mobile Receiving',
    exact: true,
    element: EditReceivingMobile,
    permissions: [MENU_MOBILE_RECEIVING],
  },
  {
    path: '/po',
    name: 'Purchase Order',
    exact: true,
    element: PurchaseOrders,
    permissions: [MENU_PURCHASE_ORDER],
  },
  {
    path: '/po/import',
    name: 'Import',
    exact: true,
    element: ImportPurchaseOrder,
    permissions: [PURCHASE_ORDER_CREATE],
  },
  {
    path: '/po/create',
    name: 'Create',
    exact: true,
    element: CreatePurchaseOrder,
    permissions: [PURCHASE_ORDER_CREATE],
  },
  {
    path: '/po/edit/:id',
    name: 'Edit',
    exact: true,
    element: EditPurchaseOrder,
    permissions: [PURCHASE_ORDER_EDIT],
  },
  {
    path: '/po/view/:id',
    name: 'View',
    exact: true,
    element: EditPurchaseOrder,
    permissions: [MENU_PURCHASE_ORDER],
  },
  {
    path: '/products/inventory',
    name: 'Inventory',
    exact: true,
    element: Inventory,
    permissions: [MENU_INVENTORY, MENU_MOBILE_INVENTORY],
  },
  {
    path: '/products/inventory/import',
    name: 'Import',
    exact: true,
    element: ImportInventory,
    permissions: [INVENTORY_CREATE],
  },
  {
    path: '/products/inventory/transfer-sku2sku',
    name: 'SKU to SKU Transfer',
    exact: true,
    element: TransferSku2Sku,
    permissions: [INVENTORY_TRANSFER_SKU2SKU],
  },
  {
    path: '/products/inventory/transfer',
    name: 'Transfer',
    exact: true,
    element: TransferInventory,
    permissions: [INVENTORY_TRANSFER],
  },
  {
    path: '/products/inventory/adjust',
    name: 'Adjust',
    exact: true,
    element: AdjustInventory,
    permissions: [INVENTORY_ADJUST],
  },
  {
    path: '/products/inventory/hold',
    name: 'Hold',
    exact: true,
    element: HoldInventory,
    permissions: [INVENTORY_HOLD],
  },
  {
    path: '/products/inventory/release',
    name: 'Release',
    exact: true,
    element: ReleaseInventory,
    permissions: [INVENTORY_RELEASE],
  },
  {
    path: '/products/inventory/location-transfer',
    name: 'Location Transfer',
    exact: true,
    element: LocationTransfer,
    permissions: [INVENTORY_LOCATION_TRANSFER],
  },
  {
    path: '/products/transactions',
    name: 'Transactions',
    exact: true,
    element: Transactions,
    permissions: [MENU_TRANSACTIONS],
  },
  {
    path: '/products/receiving-transactions',
    name: 'Receiving Transactions',
    exact: true,
    element: ReceivingTransactions,
    permissions: [MENU_RECEIVING_TRANSACTIONS],
  },
  {
    path: '/zones',
    name: 'Zones',
    exact: true,
    element: Zones,
    private: true,
    permissions: [MENU_ZONES],
  },
  {
    path: '/zones/edit/:id',
    name: 'Edit',
    exact: true,
    element: ZoneEdit,
    permissions: [ZONE_EDIT],
  },
  {
    path: '/zones/create',
    exact: true,
    name: 'Create',
    element: ZoneCreate,
    permissions: [ZONE_CREATE],
  },
  {
    path: '/system/countries',
    name: 'Countries',
    exact: true,
    element: Countries,
    permissions: [MENU_COUNTRY],
  },
  {
    path: '/system/countries/create',
    exact: true,
    name: 'Create',
    element: EditCountry,
    permissions: [SYSTEM_CREATE],
  },
  {
    path: '/system/countries/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditCountry,
    permissions: [SYSTEM_EDIT],
  },
  {
    path: '/system/languages/languages',
    name: 'languages',
    exact: true,
    element: Languages,
    permissions: [MENU_LANGUAGES],
  },
  {
    path: '/system/languages/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditLanguage,
    permissions: [SYSTEM_EDIT],
  },
  {
    path: '/system/languages/create',
    exact: true,
    name: 'Create',
    element: EditLanguage,
    permissions: [SYSTEM_CREATE],
  },
  {
    path: '/warehouses',
    name: 'Warehouses',
    exact: true,
    element: Warehouses,
    permissions: [MENU_WAREHOUSE],
  },
  {
    path: '/warehouses/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditWarehouse,
    permissions: [WAREHOUSE_EDIT],
  },
  {
    path: '/warehouses/create',
    exact: true,
    name: 'Create',
    element: EditWarehouse,
    permissions: [WAREHOUSE_CREATE],
  },
  {
    path: '/system/provinces',
    exact: true,
    name: 'Provinces',
    element: Provinces,
    permissions: [MENU_PROVINCE],
  },
  {
    path: '/system/provinces/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditProvince,
    permissions: [SYSTEM_EDIT],
  },
  {
    path: '/system/provinces/create',
    exact: true,
    name: 'Create',
    element: EditProvince,
    permissions: [SYSTEM_CREATE],
  },
  {
    path: '/system/settings/settings',
    name: 'Settings',
    exact: true,
    element: Settings,
    permissions: [MENU_SETTINGS],
  },
  {
    path: '/system/settings/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditSetting,
    permissions: [SYSTEM_EDIT],
  },
  {
    path: '/system/settings/create',
    exact: true,
    name: 'Create',
    element: EditSetting,
    permissions: [SYSTEM_CREATE],
  },
  {
    path: '/system/carriers/carriers',
    exact: true,
    name: 'Carriers',
    element: Carriers,
    permissions: [MENU_CARRIER],
  },
  {
    path: '/system/carriers/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditCarrier,
    permissions: [SYSTEM_EDIT],
  },
  {
    path: '/system/carriers/create',
    exact: true,
    name: 'Create',
    element: EditCarrier,
    permissions: [SYSTEM_CREATE],
  },
  {
    path: '/profile',
    exact: true,
    name: 'User Profile',
    element: Profile,
    permissions: [MENU_PROFILE],
  },
  {
    path: '/system/holidays',
    name: 'Holidays',
    exact: true,
    element: Holidays,
    permissions: [MENU_HOLIDAY],
  },
  {
    path: '/system/holidays/import',
    name: 'Import',
    exact: true,
    element: ImportHolidays,
    permissions: [SYSTEM_CREATE],
  },
  {
    path: '/system/holidays/create',
    exact: true,
    name: 'Create',
    element: EditHoliday,
    permissions: [SYSTEM_CREATE],
  },
  {
    path: '/system/holidays/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditHoliday,
    permissions: [SYSTEM_EDIT],
  },
  {
    path: '/system/errors',
    name: 'Errors',
    exact: true,
    element: Errors,
    permissions: [MENU_ERRORS],
  },
  {
    path: '/system/errors/create',
    exact: true,
    name: 'Create',
    element: EditError,
    permissions: [MENU_ERRORS],
  },
  {
    path: '/system/errors/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditError,
    permissions: [MENU_ERRORS],
  },
  {
    path: '/billing/invoices',
    name: 'Invoices',
    exact: true,
    element: Invoices,
    permissions: [MENU_BILLING_INVOICES],
  },
  {
    path: '/billing/invoices/edit/:id',
    name: 'Edit',
    exact: true,
    element: EditInvoice,
    permissions: [INVOICE_EDIT],
  },
  {
    path: '/billing/invoices/view/:id',
    name: 'View',
    exact: true,
    element: ViewInvoice,
    permissions: [INVOICE_DETAILS],
  },
  {
    path: '/billing/client-pricing',
    name: 'Client Pricing',
    exact: true,
    element: BillingClientPricing,
    permissions: [MENU_BILLING_CLIENT_PRICING],
  },
  {
    path: '/billing/client-pricing/create',
    name: 'Create',
    exact: true,
    element: CreateBillingClientPricing,
    permissions: [BILLING_CREATE],
  },
  {
    path: '/billing/client-pricing/edit/:id',
    name: 'Edit',
    exact: true,
    element: CreateBillingClientPricing,
    permissions: [BILLING_EDIT],
  },
  {
    path: '/billing/taxes/tax-classes',
    name: 'Tax Classes',
    exact: true,
    element: TaxClasses,
    permissions: [MENU_TAXES],
  },
  {
    path: '/billing/taxes/tax-classes/create',
    name: 'Create',
    exact: true,
    element: CreateTaxClass,
    permissions: [MENU_TAX_CLASSES],
  },
  {
    path: '/billing/taxes/tax-classes/edit/:id',
    name: 'Edit',
    exact: true,
    element: EditTaxClass,
    permissions: [MENU_TAX_CLASSES],
  },
  {
    path: '/billing/taxes/tax-rates',
    name: 'Tax Rates',
    exact: true,
    element: TaxRates,
    permissions: [MENU_TAX_RATES],
  },
  {
    path: '/billing/taxes/tax-rates/create',
    name: 'Create',
    exact: true,
    element: CreateTaxRate,
    permissions: [MENU_TAX_RATES],
  },
  {
    path: '/billing/taxes/tax-rates/edit/:id',
    name: 'Edit',
    exact: true,
    element: EditTaxRate,
    permissions: [MENU_TAX_RATES],
  },
  {
    path: '/system/departments/departments',
    name: 'Departments',
    exact: true,
    element: Departments,
    permissions: [MENU_DEPARTMENTS],
  },
  {
    path: '/system/departments/departments/create',
    name: 'Create',
    exact: true,
    element: AddDepartment,
    permissions: [SYSTEM_CREATE],
  },
  {
    path: '/system/departments/departments/edit/:id',
    name: 'Edit',
    exact: true,
    element: EditDepartment,
    permissions: [SYSTEM_EDIT],
  },
  {
    path: '/system/platforms/platforms',
    name: 'Platforms',
    exact: true,
    element: Platforms,
    permissions: [MENU_PLATFORMS],
  },
  {
    path: '/system/platforms/platforms/create',
    exact: true,
    name: 'Create',
    element: EditPlatform,
    permissions: [SYSTEM_CREATE],
  },
  {
    path: '/system/platforms/platforms/edit/:id',
    exact: true,
    name: 'Edit',
    element: EditPlatform,
    permissions: [SYSTEM_EDIT],
  },
  {
    path: '/logs/order-logs',
    exact: true,
    name: 'Order Logs',
    element: OrderLogs,
    permissions: [MENU_LOGS],
  },
  {
    path: '/logs/schedule-logs',
    exact: true,
    name: 'Schedule Logs',
    element: ScheduleLogsList,
    permissions: [MENU_LOGS],
  },
  {
    path: '/logs/schedule-item-logs',
    exact: true,
    name: 'Schedule Item Logs',
    element: ScheduleItemLogsList,
    permissions: [MENU_LOGS],
  },
  {
    path: '/logs/queue-logs',
    exact: true,
    name: 'Queue Logs',
    element: QueueLogsList,
    permissions: [MENU_LOGS],
  },
  {
    path: '/logs/failed-job-logs',
    exact: true,
    name: 'Failed Job Logs',
    element: FailedJobLogsList,
    permissions: [MENU_LOGS],
  },
]
export default routes
